import React from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';

import type { StudioDefinition, TextOperationType } from '../../domain';
import { TEXT_OPERATION_CATALOG, TEXT_OPERATION_ORDER } from './operations';

/**
 * The studio's text operations, sourced from `studio.featuredOperations` (so a
 * studio curates the dock), filtered to the 8 known kinds. Falls back to the
 * full set when the studio lists none that map to a text operation.
 */
export function resolveStudioTextOperations(studio: StudioDefinition): TextOperationType[] {
  const valid = (studio.featuredOperations ?? []).filter(k =>
    Object.prototype.hasOwnProperty.call(TEXT_OPERATION_CATALOG, k),
  ) as TextOperationType[];
  return valid.length > 0 ? valid : [...TEXT_OPERATION_ORDER];
}

export function pickDefaultTextOperation(studio: StudioDefinition): TextOperationType {
  return resolveStudioTextOperations(studio)[0] ?? 'Generate';
}

interface Props {
  studio: StudioDefinition;
  activeKind: TextOperationType;
  onSelect: (kind: TextOperationType) => void;
}

/** Operation dock for Writer's Lab — mirrors Image Lab's OperationDock. */
export const TextOperationDock: React.FC<Props> = ({ studio, activeKind, onSelect }) => {
  const operations = resolveStudioTextOperations(studio);

  const handleChange = (_e: unknown, value: TextOperationType | null) => {
    if (value) onSelect(value);
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        overflowX: 'auto',
      }}
      aria-label="Text operation dock"
    >
      <ToggleButtonGroup
        value={activeKind}
        exclusive
        onChange={handleChange}
        size="small"
        sx={{ flexWrap: 'nowrap' }}
      >
        {operations.map(kind => {
          const meta = TEXT_OPERATION_CATALOG[kind];
          const Icon = meta.icon;
          return (
            <Tooltip key={kind} title={meta.description} placement="top">
              <ToggleButton value={kind} aria-label={meta.label} sx={{ textTransform: 'none' }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Icon fontSize="small" />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {meta.label}
                  </Typography>
                </Stack>
              </ToggleButton>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};
